import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, Search, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import Icon from '../AppIcon';
import supabase from '../../utils/supabase';

const EnhancedSkillsSelector = ({ 
  selectedSkills = [], 
  onSkillsChange, 
  error,
  label = "Required Skills",
  placeholder = "Search or add skills...",
  required = false,
  maxSkills = 10,
  className = ""
}) => {
  const [dbSkills, setDbSkills] = useState([]);
  const [skillCategories, setSkillCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customSkillInput, setCustomSkillInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [customSkills, setCustomSkills] = useState([]);
  const [addingCustomSkill, setAddingCustomSkill] = useState(false);

  // Load skills and categories from database
  useEffect(() => {
    const fetchSkillsData = async () => {
      try {
        setLoading(true);
        
        // Fetch skill categories
        const { data: categories, error: categoriesError } = await supabase
          .from('skill_categories')
          .select('*')
          .order('name');

        if (categoriesError) {
          console.error('Error fetching skill categories:', categoriesError);
          return;
        }

        // Fetch skills with categories
        const { data: skills, error: skillsError } = await supabase
          .from('skills')
          .select(`
            id,
            name,
            description,
            difficulty_level,
            category_id,
            active,
            skill_categories!skills_category_id_fkey (
              id,
              name,
              icon
            )
          `)
          .eq('active', true)
          .order('name');

        if (skillsError) {
          console.error('Error fetching skills:', skillsError);
          return;
        }

        setSkillCategories(categories || []);
        setDbSkills(skills || []);
        
        // Initially expand all categories
        setExpandedCategories(new Set(categories?.map(cat => cat.id) || []));
        
      } catch (error) {
        console.error('Error in fetchSkillsData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSkillsData();
  }, []);

  // Group skills by category for display
  const skillsByCategory = useMemo(() => {
    const grouped = {};
    
    skillCategories.forEach(category => {
      grouped[category.id] = {
        ...category,
        skills: dbSkills.filter(skill => skill.category_id === category.id)
      };
    });
    
    return grouped;
  }, [dbSkills, skillCategories]);

  // Filter skills based on search term
  const filteredSkills = useMemo(() => {
    if (!searchTerm.trim()) return skillsByCategory;
    
    const filtered = {};
    Object.entries(skillsByCategory).forEach(([categoryId, categoryData]) => {
      const matchingSkills = categoryData.skills.filter(skill =>
        skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        skill.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (matchingSkills.length > 0) {
        filtered[categoryId] = {
          ...categoryData,
          skills: matchingSkills
        };
      }
    });
    
    return filtered;
  }, [skillsByCategory, searchTerm]);

  // Handle skill selection
  const handleSkillToggle = (skill) => {
    const skillName = typeof skill === 'string' ? skill : skill.name;
    const isSelected = selectedSkills.includes(skillName);
    
    if (isSelected) {
      // Remove skill
      const newSkills = selectedSkills.filter(s => s !== skillName);
      onSkillsChange(newSkills);
    } else {
      // Add skill (check max limit)
      if (selectedSkills.length >= maxSkills) {
        alert(`You can only select up to ${maxSkills} skills.`);
        return;
      }
      
      const newSkills = [...selectedSkills, skillName];
      onSkillsChange(newSkills);
    }
  };

  // Handle adding custom skill
  const handleAddCustomSkill = async () => {
    const trimmedSkill = customSkillInput.trim();
    
    if (!trimmedSkill) return;
    
    // Validate skill name - enhanced validation
    if (trimmedSkill.length < 2) {
      alert('Skill name must be at least 2 characters long.');
      return;
    }
    
    if (trimmedSkill.length > 50) {
      alert('Skill name must be less than 50 characters.');
      return;
    }
    
    // Enhanced validation - allow more characters but still secure
    if (!/^[a-zA-Z0-9\s\-\+\.\#&\/\(\)\,\'\"]+$/.test(trimmedSkill)) {
      alert('Skill name contains invalid characters. Please use only letters, numbers, spaces, and common punctuation (-, +, ., #, &, /, (), \', \", ,).');
      return;
    }
    
    // Check if skill already exists (case insensitive)
    const existsInDb = dbSkills.some(skill => 
      skill.name.toLowerCase() === trimmedSkill.toLowerCase()
    );
    
    const existsInSelected = selectedSkills.some(skill => 
      skill.toLowerCase() === trimmedSkill.toLowerCase()
    );
    
    const existsInCustom = customSkills.some(skill => 
      skill.toLowerCase() === trimmedSkill.toLowerCase()
    );
    
    if (existsInDb || existsInSelected || existsInCustom) {
      alert('This skill already exists or is already selected.');
      return;
    }
    
    // Check max limit
    if (selectedSkills.length >= maxSkills) {
      alert(`You can only select up to ${maxSkills} skills.`);
      return;
    }
    
    // Show loading state
    setAddingCustomSkill(true);
    
    try {
      // Add to custom skills and selected skills first
      setCustomSkills(prev => [...prev, trimmedSkill]);
      const newSkills = [...selectedSkills, trimmedSkill];
      onSkillsChange(newSkills);
      setCustomSkillInput('');
      
      // Try to add the skill to the database for future use
      // This runs in the background and doesn't affect the UI if it fails
      await addSkillToDatabase(trimmedSkill);
    } catch (error) {
      // Silent failure - the skill is still usable in the current session
      console.warn('Failed to add custom skill to database:', error);
    } finally {
      setAddingCustomSkill(false);
    }
  };

  // Function to safely add custom skills to the database
  const addSkillToDatabase = async (skillName) => {
    try {
      // Use the secure database function
      const { error } = await supabase.rpc('add_custom_skill', {
        skill_name: skillName
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error adding skill to database:', error);
      // Don't throw - this should not affect the user experience
    }
  };

  // Handle removing selected skill
  const handleRemoveSkill = (skillToRemove) => {
    const newSkills = selectedSkills.filter(skill => skill !== skillToRemove);
    onSkillsChange(newSkills);
    
    // Also remove from custom skills if it was custom
    setCustomSkills(prev => prev.filter(skill => skill !== skillToRemove));
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    const id = parseInt(categoryId);
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.skills-selector-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <label className="block text-sm font-medium text-text-primary">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="animate-pulse">
          <div className="h-12 bg-secondary-100 rounded-lg mb-3"></div>
          <div className="grid grid-cols-3 gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 bg-secondary-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`skills-selector-container space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-text-primary">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {/* Selected Skills Display */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-secondary-50 rounded-lg border">
          {selectedSkills.map((skill) => (
            <div
              key={skill}
              className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-full text-sm"
            >
              <span>{skill}</span>
              <button
                type="button"
                onClick={() => handleRemoveSkill(skill)}
                className="ml-1 hover:bg-primary-dark rounded-full p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <div className="text-xs text-text-muted ml-2 self-center">
            {selectedSkills.length}/{maxSkills} skills selected
          </div>
        </div>
      )}

      {/* Search and Add Custom Skill */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={handleSearchFocus}
              placeholder={placeholder}
              className="w-full px-3 py-2 pl-10 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          </div>
          
          <div className="flex gap-1">
            <input
              type="text"
              value={customSkillInput}
              onChange={(e) => setCustomSkillInput(e.target.value)}
              placeholder="Add custom skill..."
              className="px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSkill();
                }
              }}
            />
            <button
              type="button"
              onClick={handleAddCustomSkill}
              disabled={!customSkillInput.trim() || selectedSkills.length >= maxSkills || addingCustomSkill}
              className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {addingCustomSkill ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus size={16} />
                  <span>Add</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Skills Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {Object.entries(filteredSkills).length > 0 ? (
              Object.entries(filteredSkills).map(([categoryId, categoryData]) => (
                <div key={categoryId} className="border-b border-secondary-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => toggleCategory(categoryId)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-secondary-50"
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={categoryData.icon || 'FolderOpen'} size={16} />
                      <span className="font-medium text-text-primary">{categoryData.name}</span>
                      <span className="text-xs text-text-muted">
                        ({categoryData.skills.length})
                      </span>
                    </div>
                    {expandedCategories.has(parseInt(categoryId)) ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                  
                  {expandedCategories.has(parseInt(categoryId)) && (
                    <div className="px-4 pb-3">
                      <div className="grid grid-cols-1 gap-1">
                        {categoryData.skills.map((skill) => {
                          const isSelected = selectedSkills.includes(skill.name);
                          const isMaxReached = selectedSkills.length >= maxSkills && !isSelected;
                          
                          return (
                            <button
                              key={skill.id}
                              type="button"
                              onClick={() => !isMaxReached && handleSkillToggle(skill)}
                              disabled={isMaxReached}
                              className={`p-2 text-left rounded text-sm transition-colors ${
                                isSelected
                                  ? 'bg-primary text-white'
                                  : isMaxReached
                                  ? 'bg-secondary-100 text-text-muted cursor-not-allowed'
                                  : 'hover:bg-secondary-100 text-text-secondary'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="font-medium">{skill.name}</div>
                                {skill.difficulty_level && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    skill.difficulty_level === 'beginner' ? 'bg-green-100 text-green-600' :
                                    skill.difficulty_level === 'intermediate' ? 'bg-blue-100 text-blue-600' :
                                    skill.difficulty_level === 'advanced' ? 'bg-orange-100 text-orange-600' :
                                    'bg-red-100 text-red-600'
                                  }`}>
                                    {skill.difficulty_level}
                                  </span>
                                )}
                              </div>
                              {skill.description && (
                                <div className="text-xs opacity-75 mt-0.5">
                                  {skill.description}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-text-muted">
                <AlertCircle size={24} className="mx-auto mb-2 text-text-muted" />
                <p>No skills found matching "{searchTerm}"</p>
                <p className="text-xs mt-1">Try a different search term or add a custom skill above.</p>
              </div>
            )}
            
            {/* Custom Skills Section */}
            {customSkills.length > 0 && (
              <div className="border-t border-secondary-200 p-4">
                <div className="text-sm font-medium text-text-primary mb-2 flex items-center gap-1">
                  <Icon name="Plus" size={14} />
                  Custom Skills
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {customSkills.map((skill) => {
                    const isSelected = selectedSkills.includes(skill);
                    
                    return (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => handleSkillToggle(skill)}
                        className={`p-2 text-left rounded text-sm transition-colors ${
                          isSelected
                            ? 'bg-primary text-white'
                            : 'hover:bg-secondary-100 text-text-secondary'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{skill}</div>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">
                            custom
                          </span>
                        </div>
                        <div className="text-xs opacity-75">Custom skill added by you</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      {/* Helper Text */}
      <p className="text-xs text-text-muted">
        Select up to {maxSkills} skills that are required for this position. Choose from our curated skill database or add custom skills that will be saved for future use.
      </p>
    </div>
  );
};

export default EnhancedSkillsSelector;